import {
  evaluateCondition,
  failsCondition,
  getNestedConditions,
  meetsCondition,
} from '@/components/Form/helper/conditionsHelper';
import { getFormFieldKeyRegistry, getFormValueId } from '@/components/Form/helper/formFieldKeys';
import { hasValidator } from '@/components/Form/helper/validatorsHelper';
import type { FormContentItem, FormValues } from '@/components/Form/types';
import { richtextToString } from '@/helper/RichText';
import type { ServerFieldErrors } from '@/helper/validation/serverFieldErrors';
import type {
  FormCheckboxStoryblok,
  FormConditionStoryblok,
  FormDateStoryblok,
  FormFieldsetStoryblok,
  FormFileUploadStoryblok,
  FormInputStoryblok,
  FormMarketingOptInStoryblok,
  FormPageStoryblok,
  FormRadioGroupStoryblok,
  FormSectionStoryblok,
  FormSelectStoryblok,
  FormStoryblok,
  FormTextStoryblok,
  FormTextareaStoryblok,
  NewLineStoryblok,
} from '@/types/component-types-sb';
import { isOptionItemStoryblok } from '@/types/typeguards';
import dayjs from 'dayjs';

export interface FormField {
  fieldLabel?: string;
  fieldValue: string;
  fieldValueLabel: string;
  fieldValueLabels?: string[];
  fieldId: string;
  hiddenField: boolean;
  fieldType?: string;
}

/**
 * Retrieves a list of hidden field IDs.
 *
 * @param {FormStoryblok} form - The entire form object.
 * @param {FormValues} formValues - The current form values.
 * @returns {string[]} - An array of field IDs that are hidden.
 */
export const getInvisibleFields = (form: FormStoryblok, formValues: FormValues): string[] => {
  const fieldVisibility = new Map<string, boolean>();

  const traverse = (items: any[]) => {
    items.forEach((item) => {
      const fieldId = getFormValueId(item);
      if (fieldId) {
        const hasVisibleField = fieldVisibility.get(fieldId) || false;
        fieldVisibility.set(fieldId, hasVisibleField || isVisible(item, form, formValues));
      }

      if (item.fields) {
        traverse(item.fields);
      }
      if (item.content) {
        traverse(item.content);
      }
    });
  };

  if (form?.content) {
    form.content.forEach((page) => traverse(page.content || []));
  }

  return Array.from(fieldVisibility.entries())
    .filter(([, isFieldVisible]) => !isFieldVisible)
    .map(([fieldId]) => fieldId);
};

/**
 * Builds the values that may leave the browser. Formik keeps the complete draft;
 * every schema field id is present in the result, with inactive branches cleared to ''.
 */
export const getActiveFormValues = (form: FormStoryblok, formValues: FormValues): FormValues => {
  const registry = getFormFieldKeyRegistry(form);
  const activeValues: FormValues = {};
  const valueIds = new Set<string>();

  registry.forEachField(({ valueId }) => {
    valueIds.add(valueId);
  });

  for (const valueId of valueIds) {
    const stateKey = resolveStateKeyForValueId(form, valueId, formValues);
    const item = registry.getItemForStateKey(stateKey);

    if (item && isVisible(item, form, formValues)) {
      activeValues[valueId] = stateKey in formValues ? formValues[stateKey] : '';
      const labelStateKey = `${stateKey}__label`;
      if (labelStateKey in formValues) {
        activeValues[`${valueId}__label`] = formValues[labelStateKey];
      }
    } else {
      activeValues[valueId] = '';
    }
  }

  for (const [key, value] of Object.entries(formValues)) {
    if (registry.isRegisteredKey(key)) {
      continue;
    }
    activeValues[key] = value;
  }

  return activeValues;
};

export const resolveStateKeyForValueId = (form: FormStoryblok, valueId: string, formValues: FormValues): string => {
  const registry = getFormFieldKeyRegistry(form);
  const stateKeys = registry.getStateKeysForValueId(valueId);

  if (stateKeys.length === 0) {
    return valueId;
  }

  if (stateKeys.length === 1) {
    return stateKeys[0]!;
  }

  for (const stateKey of stateKeys) {
    const item = registry.getItemForStateKey(stateKey);
    if (item && isVisible(item, form, formValues)) {
      return stateKey;
    }
  }

  return stateKeys[0]!;
};

export const mapServerFieldErrorsToStateKeys = (
  form: FormStoryblok,
  fieldErrors: ServerFieldErrors,
  formValues: FormValues
): ServerFieldErrors =>
  Object.fromEntries(
    Object.entries(fieldErrors).map(([fieldId, code]) => [resolveStateKeyForValueId(form, fieldId, formValues), code])
  );

/**
 * Checks whether a page has at least one visible, renderable field or section after conditions are applied.
 */
export const pageHasVisibleContent = (
  page: FormPageStoryblok,
  form: FormStoryblok,
  formValues: FormValues
): boolean => {
  if (!page.content?.length) {
    return false;
  }

  const hasVisibleInItems = (items: any[]): boolean =>
    items.some((item) => {
      if (item.component === 'NewLine' || item.component === 'FormText') {
        return false;
      }
      if (item.component === 'FormSection' || item.component === 'FormFieldset') {
        if (!isVisible(item, form, formValues)) {
          return false;
        }
        return hasVisibleInItems(item.fields || item.content || []);
      }
      const fieldId = getFormValueId(item);
      if (fieldId && isVisible(item, form, formValues)) {
        return true;
      }
      if (item.content?.length) {
        return hasVisibleInItems(item.content);
      }
      if (item.fields?.length) {
        return hasVisibleInItems(item.fields);
      }
      return false;
    });

  return hasVisibleInItems(page.content);
};

/**
 * Returns content indices of pages that are visible and contain at least one visible field/section.
 */
export const getVisiblePageIndices = (form: FormStoryblok, formValues: FormValues): number[] => {
  if (!form?.content?.length) {
    return [];
  }

  return form.content
    .map((page, index) => ({ page, index }))
    .filter(({ page }) => isVisible(page, form, formValues) && pageHasVisibleContent(page, form, formValues))
    .map(({ index }) => index);
};

/**
 * Clamps the logical active step to the range of visible pages.
 */
export const resolveActivePageIndex = (visibleIndices: number[], activeStep: number): number => {
  if (visibleIndices.length === 0) {
    return 0;
  }
  return Math.min(Math.max(0, activeStep), visibleIndices.length - 1);
};

export const hiddenFields = (
  fields: (
    | FormPageStoryblok
    | FormSectionStoryblok
    | FormTextareaStoryblok
    | FormInputStoryblok
    | FormCheckboxStoryblok
    | FormSelectStoryblok
    | FormDateStoryblok
    | FormTextStoryblok
    | FormFileUploadStoryblok
    | NewLineStoryblok
    | FormRadioGroupStoryblok
    | FormMarketingOptInStoryblok
  )[],
  parentAccumulator: Array<string> = []
): Array<string> => {
  if (!fields) return parentAccumulator;
  fields.forEach((fieldBlok) => {
    if (fieldBlok?.component !== 'NewLine' && fieldBlok?.component !== 'FormText') {
      if (fieldBlok?.component === 'FormInput' && fieldBlok?.type === 'hidden' && fieldBlok.label && fieldBlok.id) {
        parentAccumulator.push(fieldBlok.id);
      }
      // Check if there are nested children and recursively process them
      else if (
        (fieldBlok.content || fieldBlok.fields) &&
        (Array.isArray(fieldBlok.content) || Array.isArray(fieldBlok.fields))
      ) {
        parentAccumulator = hiddenFields(fieldBlok.content || fieldBlok.fields || [], parentAccumulator);
      }
    }
  });
  return parentAccumulator;
};

export type FormFieldsResolverOptions = {
  /** When true, emit every schema field id (inactive branches as empty entries). Used for submit/export only. */
  includeInactive?: boolean;
};

const upsertResolvedField = (parentAccumulator: FormField[], entry: FormField): void => {
  const existingIndex = parentAccumulator.findIndex(({ fieldId }) => fieldId === entry.fieldId);
  if (existingIndex === -1) {
    parentAccumulator.push(entry);
    return;
  }

  const existing = parentAccumulator[existingIndex];
  const incomingHasValue = entry.fieldValue.length > 0;
  const existingIsPlaceholder = !existing.fieldValue || existing.fieldValue.length === 0;

  if (incomingHasValue && existingIsPlaceholder) {
    parentAccumulator[existingIndex] = entry;
  }
};

/**
 * This is only necessary
 * Mapping the given field id to
 */
export const formFieldsResolver = (
  fields: (
    | FormPageStoryblok
    | FormSectionStoryblok
    | FormTextareaStoryblok
    | FormInputStoryblok
    | FormCheckboxStoryblok
    | FormSelectStoryblok
    | FormDateStoryblok
    | FormTextStoryblok
    | FormFileUploadStoryblok
    | NewLineStoryblok
    | FormRadioGroupStoryblok
    | FormMarketingOptInStoryblok
  )[],
  values: FormValues,
  parentAccumulator: FormField[] = [],
  form?: FormStoryblok,
  options?: FormFieldsResolverOptions
): FormField[] => {
  if (!fields) return parentAccumulator;

  const includeInactive = options?.includeInactive ?? false;
  const registry = form ? getFormFieldKeyRegistry(form) : undefined;
  const readFieldValue = (fieldBlok: { id?: string; component?: string; _uid?: string }) => {
    const valueId = registry ? registry.getValueId(fieldBlok as FormContentItem) : fieldBlok.id;
    if (valueId && valueId in values) {
      return values[valueId];
    }
    if (registry) {
      return registry.readValue(values, fieldBlok as FormContentItem);
    }
    return undefined;
  };
  const readFieldLabelValue = (fieldBlok: { id?: string; component?: string; _uid?: string }) => {
    const valueId = registry ? registry.getValueId(fieldBlok as FormContentItem) : fieldBlok.id;
    if (valueId) {
      const mappedLabelKey = `${valueId}__label`;
      if (mappedLabelKey in values) {
        return values[mappedLabelKey];
      }
    }
    if (registry) {
      return registry.readLabelValue(values, fieldBlok as FormContentItem);
    }
    return fieldBlok.id ? values[`${fieldBlok.id}__label`] : undefined;
  };

  let marketingOptInFound = false;

  fields.forEach((fieldBlok) => {
    const visibilityConditions = filterVisibilityConditions(fieldBlok.conditions);
    const isFieldVisible = visibilityConditions.every((condition) =>
      condition.targetAction === 'show' ? evaluateCondition(condition, values) : !evaluateCondition(condition, values)
    );

    // A hidden page/section hides all of its descendants, so do not recurse
    // into branches that are inactive for the submitted values (unless exporting full schema).
    if (!isFieldVisible && !includeInactive) return;

    const fieldVisibleInForm = form ? isVisible(fieldBlok, form, values) : isFieldVisible;

    if (fieldBlok?.component !== 'NewLine' && fieldBlok?.component !== 'FormText') {
      // Handling marketing opt-in
      if (fieldBlok?.component === 'FormMarketingOptIn' && !marketingOptInFound && fieldVisibleInForm) {
        const value = typeof values['marketing_opt_in'] !== 'undefined' ? String(values['marketing_opt_in']) : '';
        upsertResolvedField(parentAccumulator, {
          fieldValue: value,
          fieldValueLabel: '',
          fieldId: 'marketing_opt_in',
          hiddenField: false,
          fieldLabel: '',
        });
        marketingOptInFound = true; // make sure the marketing_opt_in is only added once
      } else if (
        fieldBlok?.component === 'FormMarketingOptIn' &&
        !marketingOptInFound &&
        includeInactive &&
        !fieldVisibleInForm
      ) {
        upsertResolvedField(parentAccumulator, {
          fieldValue: '',
          fieldValueLabel: '',
          fieldId: 'marketing_opt_in',
          hiddenField: false,
          fieldLabel: '',
        });
        marketingOptInFound = true;
      }

      // only collect fields which have an id and are not FormPage or FormSection
      else if (
        fieldBlok.id &&
        fieldBlok.id.length > 0 &&
        fieldBlok.component !== 'FormPage' &&
        fieldBlok.component !== 'FormSection' &&
        (fieldVisibleInForm || includeInactive)
      ) {
        if (!fieldVisibleInForm && includeInactive) {
          upsertResolvedField(parentAccumulator, {
            fieldValue: '',
            fieldValueLabel: '',
            fieldId: fieldBlok.id,
            hiddenField: fieldBlok?.component === 'FormInput' && fieldBlok?.type === 'hidden',
            fieldLabel: fieldBlok.label,
            fieldType: fieldBlok.component,
          });
        } else if (fieldVisibleInForm) {
          if (
            fieldBlok?.component === 'FormRadioGroup' ||
            fieldBlok?.component === 'FormSelect' ||
            fieldBlok?.component === 'FormCheckbox'
          ) {
            const valueToLabelMap: Record<string, string> = {};

            fieldBlok.options.forEach((item) => {
              if (isOptionItemStoryblok(item)) {
                valueToLabelMap[item.value] = richtextToString(item.label);
              } else {
                valueToLabelMap[item.value] = item.label;
              }
            });

            const fieldValue = readFieldValue(fieldBlok);
            const combinedValues = Array.isArray(fieldValue) ? fieldValue.map((v) => String(v)) : [String(fieldValue)];

            // 1) Manual label override via __label always wins if present
            const dynamicLabel = readFieldLabelValue(fieldBlok);
            let fieldValueLabels: string[] = [];
            if (
              typeof dynamicLabel !== 'undefined' &&
              dynamicLabel !== null &&
              String(dynamicLabel).trim().length > 0
            ) {
              fieldValueLabels = Array.isArray(dynamicLabel)
                ? dynamicLabel.map((label) => String(label))
                : [String(dynamicLabel)];
            } else {
              // 2) Fallback: derive labels from the option map
              fieldValueLabels = combinedValues
                .map((value) => valueToLabelMap[value])
                .filter((label) => !!label && label.length > 0);
            }

            upsertResolvedField(parentAccumulator, {
              fieldValue: combinedValues.join(','),
              fieldValueLabel: fieldValueLabels.join(','),
              fieldValueLabels: fieldBlok.component === 'FormCheckbox' ? fieldValueLabels : undefined,
              fieldId: fieldBlok.id,
              hiddenField: false,
              fieldLabel: fieldBlok.label,
              fieldType: fieldBlok.component,
            });
          } else {
            const value = readFieldValue(fieldBlok) ?? '';
            let fieldValue = '';
            // Check if the value is an array and contains File objects
            if (Array.isArray(value)) {
              const fileNames = [];
              for (let i = 0; i < value.length; i++) {
                if (value[i] instanceof File) {
                  fileNames.push(value[i].name);
                }
              }
              fieldValue = fileNames.join(',');
            } else if (value instanceof File) {
              // Handle case where a single File object is directly in FormData
              fieldValue = value.name;
            } else {
              fieldValue = String(value);
              if (fieldBlok?.component === 'FormDate') {
                const parsedDate = dayjs(fieldValue);
                if (parsedDate.isValid()) {
                  fieldValue = parsedDate.format('DD.MM.YYYY');
                } else {
                  fieldValue = '';
                }
              }
            }

            upsertResolvedField(parentAccumulator, {
              fieldValue,
              fieldValueLabel: '',
              fieldId: fieldBlok.id!,
              hiddenField: fieldBlok?.component === 'FormInput' && fieldBlok?.type === 'hidden',
              fieldLabel: fieldBlok.label,
              fieldType: fieldBlok.component,
            });
          }
        }
      }

      // Check if there are nested children and recursively process them
      if (
        (fieldBlok.content || fieldBlok.fields) &&
        (Array.isArray(fieldBlok.content) || Array.isArray(fieldBlok.fields))
      ) {
        parentAccumulator = formFieldsResolver(
          fieldBlok.content || fieldBlok.fields || [],
          values,
          parentAccumulator,
          form,
          options
        );
      }
    }
  });
  return parentAccumulator;
};

export const filterValidationConditions = (
  conditions: FormConditionStoryblok[] | undefined
): FormConditionStoryblok[] => {
  if (!conditions) return [];
  return conditions.filter((condition) => ['require', 'optional'].includes(condition?.targetAction || ''));
};

export const filterVisibilityConditions = (
  conditions: FormConditionStoryblok[] | undefined
): FormConditionStoryblok[] => {
  if (!conditions) return [];
  return conditions.filter((condition) => ['show', 'hide'].includes(condition?.targetAction || ''));
};

export const isVisible = (field: any, form: FormStoryblok, formValues: FormValues): boolean => {
  const conditions: FormConditionStoryblok[] = getNestedConditions(field, form);

  const visibilityConditions = filterVisibilityConditions(conditions);

  const visible = visibilityConditions.every((condition) => {
    if (condition.targetAction === 'show') {
      return evaluateCondition(condition, formValues);
    } else {
      return !evaluateCondition(condition, formValues);
    }
  });
  return visible;
};

export const isHidden = (field: any, form: FormStoryblok, formValues: FormValues): boolean =>
  !isVisible(field, form, formValues);

export const isRequired = (field: any, form: FormStoryblok, formValues: FormValues): boolean => {
  if (!isVisible(field, form, formValues)) {
    return false;
  }

  return (
    (hasValidator('Required', field) && failsCondition('optional', field, formValues)) ||
    meetsCondition('require', field, formValues, false)
  );
};

export const isOptional = (field: any, form: FormStoryblok, formValues: FormValues): boolean =>
  !isRequired(field, form, formValues);

type FormStepContentItem = NonNullable<FormPageStoryblok['content']>[number];
type FormNestedFieldItem = NonNullable<FormSectionStoryblok['fields']>[number];
type FormTraversableItem = FormStepContentItem | FormNestedFieldItem;

const isFormContainerItem = (item: FormTraversableItem): item is FormSectionStoryblok | FormFieldsetStoryblok =>
  item.component === 'FormSection' || item.component === 'FormFieldset';

const getNestedFormContent = (item: FormTraversableItem): FormTraversableItem[] | undefined => {
  if (isFormContainerItem(item)) {
    return item.fields;
  }

  if ('content' in item && Array.isArray(item.content)) {
    return item.content as FormTraversableItem[];
  }

  return undefined;
};

/**
 * Whether the active form step has at least one visible required field.
 * Used to show the "Pflichtfelder sind mit * markiert." hint in Storyblok forms.
 *
 * Must recurse into FormSection / FormFieldset containers; required inputs live in
 * `fields`, not only at the FormPage root (`content`). Visibility and requiredness
 * are delegated to `isRequired`, which respects nested conditions.
 */
export const hasRequiredFields = (form: FormStoryblok, formValues: FormValues, activeStep: number = 0) => {
  const stepContent = form.content?.[activeStep]?.content;
  if (!stepContent) {
    return false;
  }

  const hasRequiredInItems = (items: FormTraversableItem[]): boolean =>
    items.some((item) => {
      const fieldId = getFormValueId(item);
      if (fieldId && isRequired(item, form, formValues)) {
        return true;
      }

      const nestedItems = getNestedFormContent(item);
      return nestedItems ? hasRequiredInItems(nestedItems) : false;
    });

  return hasRequiredInItems(stepContent);
};
